import { useId, useLayoutEffect, useRef, type ReactNode } from 'react';

import { cx } from '../utils/cx';

export interface CheckboxProps {
  readonly label: string;
  readonly checked?: boolean;
  readonly defaultChecked?: boolean;
  /**
   * Tri-state. `indeterminate` is not an HTML attribute — it exists only as a
   * DOM property — so it is applied through a ref below rather than JSX.
   * Needed by the "select all" header cell of a `Table` whose rows are
   * partially selected.
   */
  readonly indeterminate?: boolean;
  readonly disabled?: boolean;
  readonly name?: string;
  readonly value?: string;
  readonly describedBy?: string;
  readonly onChange?: (checked: boolean) => void;
}

/**
 * Checkbox with a visible label (§7.1 primitives).
 *
 * The prototypes hide their inputs with `class="hidden"` and paint a
 * substitute element. That is reproduced here in appearance only, never in
 * mechanism: `display:none` removes the control from the accessibility tree
 * and from keyboard focus order entirely, which would break the §7.1
 * accessibility contract ("every interactive element keyboard-reachable with
 * a visible --focus-ring"). Instead the real `<input>` is kept, and styled
 * directly via `appearance: none`, so focus, keyboard toggling, form
 * participation and screen-reader semantics are the browser's, not a
 * reimplementation.
 */
export function Checkbox({
  label,
  checked,
  defaultChecked,
  indeterminate = false,
  disabled = false,
  name,
  value,
  describedBy,
  onChange,
}: CheckboxProps): ReactNode {
  const id = useId();
  const ref = useRef<HTMLInputElement>(null);

  /**
   * `useLayoutEffect`, and with no dependency array, both on purpose.
   *
   * Layout phase because this synchronises a DOM property: a passive effect
   * runs after paint, which leaves a window in which the input is mounted but
   * still reports `indeterminate === false`. jsdom flushes effects quickly
   * enough to hide that window, so the unit suite passed while the real
   * browser did not — the Storybook test-runner caught it (résumé entry 14).
   *
   * No dependency array because React does not manage `indeterminate` itself
   * but does re-assign `checked` on the node when it updates the input, which
   * clears it. Re-asserting on every render keeps the two in step.
   */
  useLayoutEffect(() => {
    if (ref.current !== null) ref.current.indeterminate = indeterminate;
  });

  return (
    <div className="ecom-choice">
      <input
        ref={ref}
        id={id}
        type="checkbox"
        className={cx('ecom-choice__control', 'ecom-choice__control--checkbox')}
        {...(checked !== undefined ? { checked } : {})}
        {...(defaultChecked !== undefined ? { defaultChecked } : {})}
        {...(name !== undefined ? { name } : {})}
        {...(value !== undefined ? { value } : {})}
        {...(describedBy !== undefined ? { 'aria-describedby': describedBy } : {})}
        disabled={disabled}
        onChange={(event) => onChange?.(event.currentTarget.checked)}
      />
      <label htmlFor={id} className="ecom-choice__label">
        {label}
      </label>
    </div>
  );
}
